import { makeStyles } from '@material-ui/core/styles';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { color } from '../../../app/theme';
import {
  BirthdayType,
  GATriggerType,
  KeyWordFilterType,
  NodeType,
  PlatformType,
  ProviderActionType,
  ProviderType,
  TagFilterType,
  TriggerFlowModeType,
  TriggerType,
  UrlFilterType,
} from '../../../app/types';
import { checkFeatures } from '../../../app/utils';
import {
  setDataByNodeId,
  setNodeById,
  setSelectedNode,
} from '../../../features/mainSlice';
import Dropdown from '../../shared/Dropdown';

const useStyles = makeStyles(
  {
    root: {
      marginBottom: 36,
    },
    title: {
      fontSize: '15px',
      fontWeight: '500',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$3,
      marginBottom: 12,
    },
  },
  {
    name: 'Rule',
  },
);

/**
 *
 * @param { type:TriggerType, query } data
 *
 */

const Rule = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { provider, isEnabledSetting } = useSelector(
    state => state.superMarket.EC,
  );
  const { selectedNode, data: maData } = useSelector(state => state.ma);
  const { platform, nodes, edges } = maData || {};
  const { data } = selectedNode || {};
  const { type } = data || {};

  const { serviceFeatures } = useSelector(store => store.organization);

  const targetNodeId = useMemo(
    () => (edges.find(e => e?.source === selectedNode?.id) || {})?.target,
    [selectedNode],
  );

  const targetNode = useMemo(
    () => nodes.find(n => n?.id === targetNodeId),
    [targetNodeId],
  );

  const clearTargetAutoTrackProduct = async () => {
    if (targetNode?.type === NodeType?.MESSAGE) {
      await dispatch(
        setDataByNodeId(targetNode?.id, {
          ...targetNode?.data,
          messages: targetNode?.data?.messages?.map(msg => {
            return {
              ...msg,
              data: {
                ...msg?.data,
                autoTrackProduct: undefined,
              },
            };
          }),
        }),
      );
    }
  };

  const onChange = value => {
    let newNode = {};

    switch (value) {
      case TriggerType.GA:
        newNode = {
          ...selectedNode,
          data: {
            type: GATriggerType.GAVIEW,
            logicType: 'and',
            conditions: [
              {
                urlType: UrlFilterType.EQ,
                url: '',
                pageViewTimes: 1,
                staySec: null,
              },
            ],
          },
        };
        break;
      case TriggerType.ADD:
      case TriggerType.UNBLOCK:
        newNode = { ...selectedNode, data: { type: value } };
        break;
      case TriggerType.KEYWORD:
        newNode = {
          ...selectedNode,
          data: {
            type: value,
            conditions: [
              {
                type: KeyWordFilterType.KEYWORD,
                value: [''],
              },
            ],
          },
        };
        break;
      case TriggerType.TAG:
        newNode = {
          ...selectedNode,
          data: {
            type: value,
            conditions: [
              {
                type: TagFilterType.INCLUDE,
                value: [],
              },
            ],
          },
        };
        break;
      case TriggerType.OTHER:
        newNode = {
          ...selectedNode,
          data: { type: TriggerFlowModeType.BOT },
        };
        break;
      case TriggerType.BIRTHDAY:
        newNode = {
          ...selectedNode,
          data: { type: value, conditions: BirthdayType.DAY },
        };
        break;
      case provider:
        newNode = {
          ...selectedNode,
          data: {
            type: value,
            action: ProviderActionType.TOTALORDERCOUNT,
            conditions: 0,
          },
        };
        break;
      default:
        break;
    }

    if (value !== TriggerType.GA) {
      clearTargetAutoTrackProduct();
    }

    dispatch(setSelectedNode(newNode));
    dispatch(setNodeById(newNode));
  };

  const pictureTypes = useMemo(() => {
    let tempItems = [
      { text: t(`trigger.${TriggerType.ADD}`), value: TriggerType.ADD },
      { text: t(`trigger.${TriggerType.UNBLOCK}`), value: TriggerType.UNBLOCK },
      { text: t(`trigger.${TriggerType.TAG}`), value: TriggerType.TAG },
      { text: t(`trigger.${TriggerType.KEYWORD}`), value: TriggerType.KEYWORD },
      {
        text: t(`trigger.${TriggerType.BIRTHDAY}`),
        value: TriggerType.BIRTHDAY,
      },
      { text: t(`trigger.${TriggerType.OTHER}`), value: TriggerType.OTHER },
    ];

    if (platform === PlatformType.LIVECHAT) {
      tempItems = [
        {
          text: t(`trigger.${TriggerType.BIRTHDAY}`),
          value: TriggerType.BIRTHDAY,
        },
      ];
    }

    if (
      checkFeatures(serviceFeatures, 'ga4Event') &&
      (platform === PlatformType.LINE || platform === PlatformType.LIVECHAT)
    ) {
      tempItems.push({ text: t(`add-GAConditions`), value: TriggerType.GA });
    }

    if (platform !== PlatformType.LINE) {
      tempItems = tempItems.filter(item => item?.value !== TriggerType.UNBLOCK);
    }

    if (
      isEnabledSetting &&
      Object.values(ProviderType).includes(provider || '') &&
      (platform === PlatformType.LINE || platform === PlatformType.LIVECHAT)
    ) {
      tempItems.push({ text: t(`${provider}`), value: provider });
    }

    return tempItems;
  }, [platform, isEnabledSetting, provider]);

  return (
    <div className={classes.root}>
      <div className={classes.title}>{t('trigger-setting.rule')}</div>
      <Dropdown
        className={classes.dropdown}
        menuItems={pictureTypes}
        value={
          type === TriggerFlowModeType.BOT ||
          type === TriggerFlowModeType.SUPERLINK ||
          type === TriggerFlowModeType.PROCEDURE ||
          type === TriggerFlowModeType.API
            ? TriggerType.OTHER
            : type === GATriggerType.GAVIEW ||
              type === GATriggerType.GAADDTOCART ||
              type === GATriggerType.GAPURCHASE ||
              type === GATriggerType.GAREMOVEFROMCART
            ? TriggerType.GA
            : type
        }
        onChange={onChange}
      />
    </div>
  );
};

export default Rule;
